// ============================================================================
// OPENING HOURS UTILITY - PROPER HANDLING WITHOUT FALLBACKS
// ============================================================================

export interface OpeningHours {
  open_now?: boolean;
  periods?: Array<{
    close?: { day: number; time: string };
    open: { day: number; time: string };
  }>;
  weekday_text?: string[];
}

export interface OpeningHoursDisplayData {
  todayHours: string | null;
  isCurrentlyOpen: boolean | null; // null means unknown
  hasValidData: boolean;
}

/**
 * Get today's opening hours display data
 * CRITICAL: Never assumes open/closed status without valid data
 * Handles both proper OpeningHours objects and legacy boolean values
 */
export function getTodayOpeningHours(openingHours: OpeningHours | boolean | undefined | null): OpeningHoursDisplayData {
  // Add debugging to understand the data structure
  console.log('🕒 Opening hours input:', openingHours);
  console.log('🕒 URGENT DEBUG - Current time:', new Date().toString());
  console.log('🕒 URGENT DEBUG - Current time (Swedish):', new Date().toLocaleString('sv-SE', {timeZone: 'Europe/Stockholm'}));

  // Return null state if no data - NEVER assume open or closed
  if (!openingHours) {
    console.log('🕒 No opening hours data provided');
    return {
      todayHours: null,
      isCurrentlyOpen: null,
      hasValidData: false
    };
  }

  // Handle legacy boolean values (from old fallback code) - treat as unknown
  if (typeof openingHours === 'boolean') {
    console.log('🕒 Legacy boolean opening hours detected:', openingHours);
    return {
      todayHours: null,
      isCurrentlyOpen: null,
      hasValidData: false
    };
  }

  const today = new Date().getDay(); // 0 = Sunday, 1 = Monday, etc.

  // Try to get today's hours from weekday_text (most reliable)
  let todayHours: string | null = null;
  if (openingHours.weekday_text && openingHours.weekday_text.length >= 7) {
    todayHours = openingHours.weekday_text[today] || null;
    console.log('🕒 Found weekday_text hours for today:', todayHours);
  } else {
    console.log('🕒 No weekday_text found or insufficient data:', openingHours.weekday_text);

    // Try alternative data structures - sometimes the data comes in different formats
    // Check if there's a direct hours string or other format
    const anyOpeningHours = openingHours as any;
    if (anyOpeningHours.hours) {
      todayHours = anyOpeningHours.hours;
      console.log('🕒 Found hours field:', todayHours);
    } else if (anyOpeningHours.opening_hours && typeof anyOpeningHours.opening_hours === 'string') {
      todayHours = anyOpeningHours.opening_hours;
      console.log('🕒 Found opening_hours string field:', todayHours);
    } else if (anyOpeningHours.text) {
      todayHours = anyOpeningHours.text;
      console.log('🕒 Found text field:', todayHours);
    } else {
      console.log('🕒 No alternative hours format found. Available fields:', Object.keys(openingHours));
    }
  }

  // Determine if currently open by checking actual hours vs current time
  let isCurrentlyOpen: boolean | null = null;

  if (todayHours !== null) {
    // Try to parse the actual opening hours and compare with current time
    isCurrentlyOpen = isWithinOpeningHours(todayHours);

    // NEVER FALLBACK TO FAKE DATA - if we can't parse hours, status remains null (unknown)
    // This prevents lying about open/closed status when we don't have valid data
  }

  const result = {
    todayHours,
    isCurrentlyOpen,
    hasValidData: todayHours !== null
  };

  console.log('🕒 Final opening hours result:', result);
  return result;
}

/**
 * Format today's hours for display
 * Fixes formatting issues and converts to 24-hour format
 */
export function formatTodayHours(todayHours: string | null): string {
  if (!todayHours) {
    return 'Hours not available';
  }

  // Clean up the Google format (e.g., "Monday: 9:00 AM – 10:00 PM" -> "9:00 AM – 10:00 PM")
  let formattedHours = todayHours;
  const colonIndex = formattedHours.indexOf(':');
  if (colonIndex !== -1) {
    formattedHours = formattedHours.substring(colonIndex + 1).trim();
  }

  // Fix encoding issues - replace question marks with proper characters
  formattedHours = formattedHours
    .replace(/\?/g, '') // Remove question marks
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();

  // Convert to 24-hour format
  formattedHours = convertTo24HourFormat(formattedHours);

  return formattedHours;
}

/**
 * Convert time from 12-hour to 24-hour format
 */
function convertTo24HourFormat(timeString: string): string {
  // Handle different dash types and normalize
  const normalizedTime = timeString
    .replace(/[–—−]/g, '-') // Replace different dash types with regular dash
    .replace(/\s*-\s*/g, ' - '); // Normalize spacing around dashes

  // Pattern to match times like "11:00 AM - 8:00 PM" or "9:00AM-10:00PM"
  const timePattern = /(\d{1,2}):(\d{2})\s*(AM|PM)\s*-\s*(\d{1,2}):(\d{2})\s*(AM|PM)/i;
  const match = normalizedTime.match(timePattern);

  if (match) {
    const [, startHour, startMin, startAmPm, endHour, endMin, endAmPm] = match;

    // Convert start time
    let startHour24 = parseInt(startHour);
    if (startAmPm.toUpperCase() === 'PM' && startHour24 !== 12) {
      startHour24 += 12;
    } else if (startAmPm.toUpperCase() === 'AM' && startHour24 === 12) {
      startHour24 = 0;
    }

    // Convert end time
    let endHour24 = parseInt(endHour);
    if (endAmPm.toUpperCase() === 'PM' && endHour24 !== 12) {
      endHour24 += 12;
    } else if (endAmPm.toUpperCase() === 'AM' && endHour24 === 12) {
      endHour24 = 0;
    }

    // Format with leading zeros
    const startTime = `${startHour24.toString().padStart(2, '0')}:${startMin}`;
    const endTime = `${endHour24.toString().padStart(2, '0')}:${endMin}`;

    return `${startTime} - ${endTime}`;
  }

  // If no AM/PM pattern found, return cleaned string
  return normalizedTime;
}

/**
 * Check if current time is within the restaurant's opening hours
 */
function isWithinOpeningHours(todayHours: string): boolean | null {
  try {
    // Get current time
    const now = new Date();
    const currentHours = now.getHours();
    const currentMinutes = now.getMinutes();
    const currentTimeInMinutes = currentHours * 60 + currentMinutes;

    console.log('🕒 Time check - Current time:', `${currentHours.toString().padStart(2, '0')}:${currentMinutes.toString().padStart(2, '0')}`, `(${currentTimeInMinutes} minutes)`);
    console.log('🕒 Time check - Checking hours string:', todayHours);

    // Parse the hours string to extract opening and closing times
    // Handle both 12-hour and 24-hour formats with flexible spacing, dashes, and encoding issues
    const timePattern12 = /(\d{1,2}):(\d{2})\s*\??\s*(AM|PM)\s*\??\s*[–—−-]\s*\??\s*(\d{1,2}):(\d{2})\s*\??\s*(AM|PM)/i;
    const timePattern24 = /(\d{1,2}):(\d{2})\s*\??\s*[–—−-]\s*\??\s*(\d{1,2}):(\d{2})(?!\s*\??\s*(AM|PM))/i;

    let openTimeMinutes: number | null = null;
    let closeTimeMinutes: number | null = null;

    // Try 12-hour format first
    const match12 = todayHours.match(timePattern12);
    if (match12) {
      const [, openHour, openMin, openAmPm, closeHour, closeMin, closeAmPm] = match12;

      // Convert opening time
      let openHour24 = parseInt(openHour);
      if (openAmPm.toUpperCase() === 'PM' && openHour24 !== 12) {
        openHour24 += 12;
      } else if (openAmPm.toUpperCase() === 'AM' && openHour24 === 12) {
        openHour24 = 0;
      }

      // Convert closing time
      let closeHour24 = parseInt(closeHour);
      if (closeAmPm.toUpperCase() === 'PM' && closeHour24 !== 12) {
        closeHour24 += 12;
      } else if (closeAmPm.toUpperCase() === 'AM' && closeHour24 === 12) {
        closeHour24 = 0;
      }

      openTimeMinutes = openHour24 * 60 + parseInt(openMin);
      closeTimeMinutes = closeHour24 * 60 + parseInt(closeMin);

      console.log('🕒 Time check - Parsed 12-hour format:');
      console.log('🕒   Opening time:', `${openHour24.toString().padStart(2, '0')}:${openMin}`, `(${openTimeMinutes} minutes)`);
      console.log('🕒   Closing time:', `${closeHour24.toString().padStart(2, '0')}:${closeMin}`, `(${closeTimeMinutes} minutes)`);
    } else {
      // Try 24-hour format
      const match24 = todayHours.match(timePattern24);
      if (match24) {
        const [, openHour, openMin, closeHour, closeMin] = match24;
        openTimeMinutes = parseInt(openHour) * 60 + parseInt(openMin);
        closeTimeMinutes = parseInt(closeHour) * 60 + parseInt(closeMin);

        console.log('🕒 Time check - Parsed 24-hour format:');
        console.log('🕒   Opening time:', `${openHour}:${openMin}`, `(${openTimeMinutes} minutes)`);
        console.log('🕒   Closing time:', `${closeHour}:${closeMin}`, `(${closeTimeMinutes} minutes)`);
      }
    }

    // If we couldn't parse the times, return null (unknown)
    if (openTimeMinutes === null || closeTimeMinutes === null) {
      console.log('🕒 Time check - Could not parse opening/closing times');
      return null;
    }

    // Handle overnight hours (e.g., 22:00 - 02:00)
    if (closeTimeMinutes < openTimeMinutes) {
      // Restaurant is open past midnight
      const isOpen = currentTimeInMinutes >= openTimeMinutes || currentTimeInMinutes <= closeTimeMinutes;
      console.log('🕒 Time check - Overnight hours detected. Is open:', isOpen);
      return isOpen;
    } else {
      // Normal hours (e.g., 11:00 - 20:00)
      const isOpen = currentTimeInMinutes >= openTimeMinutes && currentTimeInMinutes <= closeTimeMinutes;
      console.log('🕒 Time check - Normal hours. Is open:', isOpen);
      console.log('🕒   Current time is between', `${Math.floor(openTimeMinutes/60)}:${(openTimeMinutes%60).toString().padStart(2,'0')}`, 'and', `${Math.floor(closeTimeMinutes/60)}:${(closeTimeMinutes%60).toString().padStart(2,'0')}`, '?', isOpen);
      console.log('🕒 URGENT DEBUG - Detailed comparison:');
      console.log('🕒   currentTimeInMinutes:', currentTimeInMinutes, '>=', openTimeMinutes, '=', currentTimeInMinutes >= openTimeMinutes);
      console.log('🕒   currentTimeInMinutes:', currentTimeInMinutes, '<=', closeTimeMinutes, '=', currentTimeInMinutes <= closeTimeMinutes);
      console.log('🕒   BOTH conditions AND result:', isOpen);
      return isOpen;
    }
  } catch (error) {
    console.warn('Error parsing opening hours:', todayHours, error);
    return null; // Return unknown if parsing fails
  }
}

/**
 * Get the appropriate CSS classes for open/closed indicator
 */
export function getOpenStatusClasses(isCurrentlyOpen: boolean | null): string {
  if (isCurrentlyOpen === null) {
    // Unknown status - neutral styling
    return 'bg-gray-100 text-gray-700';
  }

  return isCurrentlyOpen
    ? 'bg-green-100 text-green-800'
    : 'bg-red-100 text-red-800';
}

/**
 * Get the status text to display
 */
export function getOpenStatusText(isCurrentlyOpen: boolean | null): string {
  if (isCurrentlyOpen === null) {
    return 'Unknown';
  }

  return isCurrentlyOpen ? 'Open' : 'Closed';
}